import type { UnknownObject } from '~/shared/interfaces'
import { isString } from '~/shared/utils/guards'
import type { RouteLocation } from 'vue-router'

// Проверяет, является ли значение null, undefined или пустой строкой
export function isNullOrEmpty(obj: unknown): boolean {
  return obj === null || obj === undefined || obj === ''
}

export function clearEmptyFields<T>(
  initial: Record<string, T | null | undefined>
): Record<string, T> {
  return Object.fromEntries(
    Object.entries(initial).filter((entry): entry is [string, T] => !isNullOrEmpty(entry[1]))
  )
}

// Возвращает значение или дефолтное, если первое пустое
export function getValue<T>(value: T | undefined, defaultValue: T): T {
  return isNullOrEmpty(value) ? defaultValue : (value as T)
}

// Возвращает первое непустое значение из списка или дефолтное
export function getAnyValue<T>(defaultValue: T, ...values: (T | undefined)[]): T {
  return values.find(val => !isNullOrEmpty(val)) || defaultValue
}

// Заменяет плейсхолдеры ${key} на значения из объекта
export function replaceParams(str: string, params: UnknownObject): string {
  return str.replace(/\${(.+?)}/g, (_, expression) => {
    const value = params[expression]
    return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
  })
}

// Форматирует число, добавляя пробелы между разрядами (22000 → 22 000)
export function formatSalary(salary: string) {
  return salary.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ')
}

// Склоняет слова в зависимости от числа (год/года/лет)
export function declOfNum(n: number, titles: [string, string, string]): string {
  return titles[
    n % 10 === 1 && n % 100 !== 11
      ? 0
      : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)
        ? 1
        : 2
  ]
}

// Проверяет, является ли приватной страницей аккаунта
export function isPrivateRoute(route: RouteLocation | string): boolean {
  const value = isString(route) ? route : route.path
  return /^\/account(\/|$)/.test(value)
}

// Оставляет в строке только цифры
export function onlyDigits(value: string) {
  return value.toString().replace(/\D/g, '')
}

// Форматирует телефон в виде +X (XXX) XXX-XX-XX
export function formatPhone(phone: string) {
  const formattedPhone = onlyDigits(phone)

  const region = formattedPhone.substr(0, 1)
  const operator = formattedPhone.substr(1, 3)
  const numberPart1 = formattedPhone.substr(4, 3)
  const numberPart2 = formattedPhone.substr(7, 2)
  const numberPart3 = formattedPhone.substr(9, 2)

  return `+${region} (${operator}) ${numberPart1}-${numberPart2}-${numberPart3}`
}
